import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppNotification, InboxGroup } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';
import { useAlerts } from '@/lib/useAlerts';
import { useInbox } from './useInbox';
import { setSoundMuted, soundMuted } from '@/lib/chime';

/**
 * The console's one notification source.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHAT WAS WRONG
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every desk in this console knew its own count, and told nobody. Support knew
 * how many conversations were waiting; refunds knew how many claims; the
 * pharmacy desk knew how many businesses could not trade until somebody
 * looked. You found out by opening each one in turn, which means you found out
 * about the ones you thought to check.
 *
 * The bell that was here read the `notifications` table — and nothing has ever
 * written a staff notification, because the method for it (`Notifier::toStaff`)
 * was written and never called. So the console had a bell that was, by
 * construction, always empty.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHY ONE BELL AND NOT TWO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The obvious build is a second bell beside the first: work here, messages
 * there. Two bells with two numbers is two things to check, which is the
 * problem this is meant to solve rather than a smaller version of it.
 *
 * So one control, one number, and two sections under it:
 *
 *   WAITING FOR YOU   derived from state, shared with everyone who holds the
 *                     permission, cannot be dismissed, goes down when the work
 *                     is done.
 *
 *   RECENT            the ordinary notification rows, addressed to this person,
 *                     read once. Kept because an admin is also a human being
 *                     with orders of their own.
 *
 * The two are different KINDS of thing and the panel says so, because
 * "4 refund claims" and "your order was delivered" want completely different
 * responses and mixing them into one list makes both easier to ignore.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  AND WHY THE BADGE IS NOT THE SUM OF THE ROWS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * An adverse-reaction report is a refund claim, so it is in two desks. The
 * server decides what the total means (see `subset_of`) and this trusts it. A
 * badge that over-counts is a badge people stop reading inside a week.
 */
export function InboxBell() {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data: inbox } = useInbox();

  const { data: personal } = useQuery({
    queryKey: ['notifications', 'panel'],
    queryFn: ({ signal }) => api.notifications(false, signal),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 20_000,
  });

  const waiting = inbox?.total ?? 0;
  const overdue = inbox?.overdue ?? 0;
  const unread = personal?.unread ?? 0;
  const total = waiting + unread;

  // Sound on an increase, never on a first reading, and the count in the tab
  // title for whoever has this console behind three other tabs.
  useAlerts(total);

  const readAll = useMutation({
    mutationFn: () => api.readAllNotifications(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const [muted, setMuted] = useState(soundMuted);

  useEffect(() => {
    if (!open) return;

    const away = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', away);

    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  // Work first, and within work the desks that have something. A zero row at
  // the top of the list is a row somebody's eye has to step over every time.
  const groups = (inbox?.groups ?? []).filter((group) => group.count > 0);
  const quiet = (inbox?.groups ?? []).filter((group) => group.count === 0);

  return (
    <div ref={wrap} className="relative">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        data-testid="inbox-bell"
        data-inbox-total={total}
        aria-label={
          total === 0
            ? 'Nothing waiting'
            : `${total} waiting${overdue > 0 ? `, ${overdue} overdue` : ''}`
        }
        aria-expanded={open}
        className="relative flex min-h-11 items-center gap-2 rounded-btn px-2.5 text-[12.5px] font-semibold text-muted hover:text-ink"
      >
        <Icon name="bell" size={18} strokeWidth={1.9} />

        {total > 0 ? (
          <>
            {/* The number, not just a dot. "Something is waiting" sends
                somebody looking; "14 waiting, 3 overdue" tells them whether to
                look now. */}
            <span
              data-testid="inbox-count"
              className={cn(
                'rounded-full px-1.5 py-[1px] font-mono text-[11px] font-bold tabular-nums text-white',
                overdue > 0 ? 'bg-danger' : 'bg-blue',
              )}
            >
              {total > 99 ? '99+' : total}
            </span>
            <span className="hidden sm:inline">waiting</span>
          </>
        ) : (
          <span className="hidden text-faint sm:inline">All clear</span>
        )}
      </button>

      {open ? (
        <div
          data-testid="inbox-panel"
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-[min(92vw,380px)] overflow-hidden rounded-card border border-line bg-white shadow-md"
        >
          <div className="flex items-center justify-between border-b border-line-2 px-3.5 py-2.5">
            <span className="font-display text-[13px] font-bold text-ink">
              {total === 0 ? 'Nothing waiting' : `${total} waiting`}
            </span>

            <button
              type="button"
              data-testid="inbox-sound"
              onClick={() => {
                const next = !muted;
                setSoundMuted(next);
                setMuted(next);
              }}
              aria-label={muted ? 'Turn the sound on' : 'Turn the sound off'}
              title={muted ? 'Sound is off' : 'Sound is on'}
              className="grid h-8 w-8 place-items-center rounded-btn text-faint hover:text-ink"
            >
              <Icon name={muted ? 'wifi-off' : 'bell'} size={14} strokeWidth={2} />
            </button>
          </div>

          <div className="max-h-[min(72vh,520px)] overflow-y-auto">
            {/* ── WAITING FOR YOU ─────────────────────────────────────── */}
            <Section label="Waiting for you" />

            {groups.length === 0 ? (
              <p className="m-0 px-3.5 py-3 text-[12px] leading-relaxed text-faint">
                Every desk you cover is clear. This counts what is still to do, not what you
                have read — so it is empty because the work is done.
              </p>
            ) : (
              groups.map((group) => (
                <DeskRow key={group.key} group={group} onGo={() => setOpen(false)} />
              ))
            )}

            {/* The clear desks, named rather than hidden. A desk that vanishes
                at zero is a desk somebody wonders whether they imagined — and
                "nothing waiting" is information when you were about to go and
                check. */}
            {quiet.length > 0 ? (
              <details className="border-t border-line-2">
                <summary className="cursor-pointer list-none px-3.5 py-2 text-[11px] font-semibold text-faint hover:text-muted">
                  {quiet.length} {quiet.length === 1 ? 'desk is' : 'desks are'} clear
                </summary>
                <ul className="m-0 list-none px-3.5 pb-2.5 pt-0">
                  {quiet.map((group) => (
                    <li
                      key={group.key}
                      className="flex items-baseline justify-between gap-3 py-[3px] text-[11.5px] text-faint"
                    >
                      <span className="truncate">{group.label}</span>
                      <span className="shrink-0 text-[10.5px]">{group.empty}</span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {/* ── RECENT ──────────────────────────────────────────────── */}
            <div className="border-t border-line">
              <div className="flex items-center justify-between pr-3">
                <Section label="Recent" />
                {unread > 0 ? (
                  <button
                    type="button"
                    onClick={() => readAll.mutate()}
                    disabled={readAll.isPending}
                    className="text-[11px] font-semibold text-blue disabled:opacity-50"
                  >
                    Mark all read
                  </button>
                ) : null}
              </div>

              {(personal?.notifications ?? []).length === 0 ? (
                <p className="m-0 px-3.5 py-3 text-[12px] text-faint">Nothing addressed to you.</p>
              ) : (
                (personal?.notifications ?? [])
                  .slice(0, 8)
                  .map((one) => (
                    <PersonalRow key={one.id} notification={one} onGo={() => setOpen(false)} />
                  ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Section({ label }: { label: string }) {
  return (
    <p className="m-0 px-3.5 pb-1 pt-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
      {label}
    </p>
  );
}

function DeskRow({ group, onGo }: { group: InboxGroup; onGo: () => void }) {
  return (
    <Link
      to={group.url}
      onClick={onGo}
      data-testid={`inbox-desk-${group.key}`}
      className="flex items-center gap-3 px-3.5 py-2.5 no-underline hover:bg-paper"
    >
      <span
        className={cn(
          'grid h-8 w-8 shrink-0 place-items-center rounded-[9px]',
          group.overdue > 0 ? 'bg-danger-tint text-danger' : 'bg-blue-tint text-blue',
        )}
      >
        <Icon name={group.icon as IconName} size={15} strokeWidth={1.9} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-semibold text-ink">{group.label}</span>
        <span className="block text-[11px] text-muted">
          {group.count} {group.count === 1 ? 'waiting' : 'waiting'}
          {/* Named, not coloured. A red tint tells somebody something is wrong
              and not what, and this is the clause that makes them open it
              first. */}
          {group.overdue > 0 ? (
            <span className="font-semibold text-danger"> · {group.overdue} overdue</span>
          ) : null}
        </span>
      </span>

      <span
        className={cn(
          'shrink-0 rounded-full px-2 py-[2px] font-mono text-[11px] font-bold tabular-nums',
          group.overdue > 0 ? 'bg-danger text-white' : 'bg-blue text-white',
        )}
      >
        {group.count > 99 ? '99+' : group.count}
      </span>
    </Link>
  );
}

function PersonalRow({
  notification,
  onGo,
}: {
  notification: AppNotification;
  onGo: () => void;
}) {
  const queryClient = useQueryClient();

  const read = useMutation({
    mutationFn: () => api.readNotification(notification.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const body = (
    <>
      <span className="block text-[12.5px] font-semibold text-ink">{notification.title}</span>
      {notification.body ? (
        <span className="block text-[11.5px] leading-snug text-muted">{notification.body}</span>
      ) : null}
    </>
  );

  const shell = cn(
    'flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left no-underline hover:bg-paper',
    notification.is_unread && 'bg-blue/[0.04]',
  );

  const dot = (
    <span
      className={cn(
        'mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full',
        notification.is_unread ? 'bg-blue' : 'bg-transparent',
      )}
    />
  );

  // A notification with nowhere to go is not a link. Wrapping it in an anchor
  // to nothing gives a screen reader a control that does not work.
  if (!notification.url) {
    return (
      <button type="button" onClick={() => read.mutate()} className={shell}>
        {dot}
        <span className="min-w-0 flex-1">{body}</span>
      </button>
    );
  }

  return (
    <Link
      to={notification.url}
      onClick={() => {
        read.mutate();
        onGo();
      }}
      className={shell}
    >
      {dot}
      <span className="min-w-0 flex-1">{body}</span>
    </Link>
  );
}
